import { useEffect, useState } from 'react';
import { app, describeError, go } from '../state/app';
import { createCommission, previewCommission, type CommissionPreview } from '../api/endpoints';
import { once } from '../lib/once';
import { commissionPreviewKey } from '../lib/keys';
import { PreviewFilm } from '../components/PreviewFilm';
import { getPreview, getPricing, type EpisodeLength } from '../api/endpoints';
import {
  DEFAULT_PEEK_SECONDS,
  peek,
  peekKey,
  peekPremise,
  peekStarted,
  type PeekInput,
} from '../state/speculate';
import { ApiError } from '../api/client';
import { poll, type PollHandle } from '../lib/poll';
import { mediaUrl } from '../lib/media';
import { suggestName } from '../lib/names';

// The fallback shown only if /pricing cannot be reached — one 8s segment,
// which is what the server makes when no length is sent. Every other number on
// this screen comes from the server, because the app must never quote a price
// it is not about to be charged.
//
// `seconds` is not its own literal: state/speculate pre-warms the peek at the
// default length before this screen exists, and a 8-here/9-there disagreement
// would mean the pre-warm is for an episode the parent is not being offered,
// so the summary silently ignores it and asks for a second one.
const FALLBACK: EpisodeLength = {
  label: 'A taster',
  seconds: DEFAULT_PEEK_SECONDS,
  segments: 1,
  uniqueShots: 1,
  credits: 4,
};

/**
 * Screen 4. The premise comes from the planner, not the parent — "never block
 * on input". A steer field belongs behind an "adjust" link, off the main path.
 */
export function Summary() {
  const [tokens, setTokens] = useState<number | null | undefined>(undefined);
  const [lengths, setLengths] = useState<EpisodeLength[]>([]);
  const [chosen, setChosen] = useState<EpisodeLength>(FALLBACK);

  useEffect(() => {
    let alive = true;
    getPricing()
      .then((rows) => {
        if (!alive || rows.length === 0) return;
        setLengths(rows);
        // The shortest is the default: the cheapest thing that proves the
        // product is the right first purchase to offer.
        setChosen(rows[0] ?? FALLBACK);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  const { entry, characterId, character, chosenIndex, characterName } = app.use();
  // `undefined` is NOT null here: null is "the planner had nothing to say",
  // undefined is "we have not asked yet". The peek's key contains the premise,
  // so offering a peek before this settles can start one for the fallback
  // scene and then discover the planner's — two scenes, two tokens.
  const [preview, setPreview] = useState<CommissionPreview | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!characterId) return;
    let alive = true;
    // Collapses onto the preview NameIt — and now the candidate screen —
    // already started; free either way.
    once(commissionPreviewKey(characterId), () => previewCommission(characterId))
      .then((value) => alive && setPreview(value))
      .catch(() => alive && setPreview(null));
    return () => {
      alive = false;
    };
  }, [characterId]);

  const candidate = character?.candidates?.[chosenIndex ?? 0];
  const hero = candidate?.url ?? mediaUrl(candidate?.key ?? candidate?.storageKey) ?? entry?.portraitUrl;
  const premise = peekPremise(preview);
  const named = characterName || suggestName(characterId ?? undefined);

  // The request is the SCENE and nothing else. It used to be
  // `${named} — ${premise}`, and the name in it was not cosmetic: plan.py's
  // _scene_action() canonicalises this exact string into the shot cache's
  // action tag, so 'Tumble — a gentle first adventure' and 'Pip — a gentle
  // first adventure' are different partitions. Every family got a private
  // cache keyed on their child's character name, and the commonest scene in
  // the product could never be reused across families.
  //
  // The name is not lost — plan.py loads it off the character row and puts it
  // in base_tags["name"], which is where identity belongs. plan.py:376 is
  // emphatic that it must never reach a prompt (Veo 3.1 has native audio and
  // will try to say it), so handing it one in the request was working against
  // the planner rather than with it.
  //
  // It also makes the free peek plan the SAME scene it is about to sell:
  // SeeItFirst below has always been passed the bare premise, so before this
  // the peek and the purchase landed in different cache partitions.
  const request = premise;

  // What the sketch on this screen is a sketch OF. state/speculate built the
  // identical object when the child tapped the tile, which is why the peek is
  // usually already made by the time this renders — and why a parent who went
  // back and tapped a DIFFERENT tile gets a different key here, so the sketch
  // fetched for the abandoned tile is never the one they are shown.
  const peekInput: PeekInput | null = characterId
    ? { characterId, candidateIndex: chosenIndex ?? 0, request, seconds: chosen.seconds }
    : null;

  const commission = async () => {
    if (!characterId) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createCommission({
        request,
        characterId,
        targetSeconds: chosen.seconds,
        previewId: preview?.previewId,
      });
      app.set({ commissionId: created.commissionId, commission: null });
      go('commission');
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="pane-img">
        {hero ? <img src={hero} alt="" /> : <div className="placeholder">✨</div>}
      </div>
      <div className="pane-q">
        <button className="backbtn" onClick={() => go('name')}>
          ‹ back
        </button>
        <h1>{named} is ready ✨</h1>

        {/* What happens, in a sentence. No shot counts, no cache-hit counts,
            no "planner's pick" — a parent is buying a little film, not a
            production plan. */}
        <p className="sub">{premise}</p>

        {lengths.length > 1 && (
          <div className="lengths">
            <div className="lengths-q">How long?</div>
            <div className="lengths-row">
              {lengths.map((row) => (
                <button
                  key={row.seconds}
                  className={`length${row.seconds === chosen.seconds ? ' on' : ''}`}
                  onClick={() => setChosen(row)}
                >
                  <b>{row.label}</b>
                  <span>{row.credits} credits</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Held back until the premise has settled, and keyed on the whole
            peek identity rather than just the length: the request string is
            part of what the token buys, so mounting before we know it can
            start a peek for the fallback scene and then need a second one for
            the planner's. Remounting on a length change is the old
            key={chosen.seconds} behaviour, subsumed. */}
        {preview !== undefined && peekInput && (
          <SeeItFirst key={peekKey(peekInput)} input={peekInput} onTokens={setTokens} />
        )}

        {/* The length and its price are on the chosen pill and on the button.
            A third copy in the middle was noise, and it pushed the button off
            the bottom of a narrow screen. */}
        {tokens !== null && tokens !== undefined && (
          <div className="summary muted">
            {tokens} free peek{tokens === 1 ? '' : 's'} left
          </div>
        )}

        {error && <div className="err">{error}</div>}

        <button className="big" onClick={commission} disabled={busy}>
          {busy ? 'holding credits…' : `Make it — ${chosen.credits} credits`}
        </button>

        <div className="legend">
          You’ll see it first — nothing reaches your child until you approve
          it.
        </div>
      </div>
    </>
  );
}


/**
 * The free preview, offered before the parent is asked to spend.
 *
 * Costs a preview token rather than credits. An empty bucket comes back as a
 * 402 — a normal state with a remedy, not an error — so it is rendered as an
 * explanation rather than a failure.
 *
 * IT MAY ALREADY BE RUNNING WHEN THIS MOUNTS. The wizard starts one the moment
 * the child taps a tile, so it has had the whole naming screen to render while
 * the parent was typing — often it is finished. Adopting it is not a second
 * request: speculate.peek() is memoized on peekKey, so the call below hands
 * back the promise the candidate screen created. `peekStarted` only decides
 * whether to show the button or go straight to waiting.
 *
 * When nothing was pre-warmed — the parent changed the length, or went back
 * and picked a different tile, or the pre-warm failed — this is exactly the
 * screen it always was: a button, and a token spent only when it is pressed.
 */
function SeeItFirst({
  input,
  onTokens,
}: {
  /** Character, tile, scene and length. A peek of a different length is a peek
      of a different episode — a half-hour film gets twenty shots, a taster
      gets one — which is why `seconds` is part of the identity. */
  input: PeekInput;
  onTokens: (tokens: number | null) => void;
}) {
  const [state, setState] = useState<'idle' | 'making' | 'ready' | 'spent' | 'error'>(
    // Read once, at mount. The parent must never watch a button turn into a
    // spinner on its own; either it was already running when they got here or
    // it waits for them to ask.
    () => (peekStarted(input) ? 'making' : 'idle'),
  );
  const [stills, setStills] = useState<string[]>([]);
  const [perStill, setPerStill] = useState(2.5);

  useEffect(() => {
    if (state !== 'making') return;
    let alive = true;
    // THE POLL USED TO LEAK. `start` was async and returned `() =>
    // handle.cancel()`, which React never sees — an async function returns a
    // promise, not a cleanup — so the interval outlived the screen. That was
    // survivable while a peek could only begin on a click; now that this
    // component can mount already-waiting and be unmounted by the length
    // picker, it is a poll per mount against a route with a 3-minute timeout.
    let handle: PollHandle | null = null;

    peek(input, false).then(
      (started) => {
        if (!alive) return;
        onTokens(started.previewTokens);
        handle = poll({
          fetch: () => getPreview(started.previewId),
          done: (value) =>
            value.status === 'preview_ready' || value.status === 'failed',
          onValue: (value) => {
            if (value.status !== 'preview_ready') return;
            setStills(value.animaticStills ?? []);
            setPerStill(value.animaticSecondsPerStill || 2.5);
            setState('ready');
          },
          onError: () => setState('error'),
          onTimeout: () => setState('error'),
          intervalMs: 2000,
          timeoutMs: 3 * 60 * 1000,
        });
      },
      (err) => {
        if (!alive) return;
        // 402 is the bucket, not a fault. Anything else is.
        const empty = err instanceof ApiError && err.status === 402;
        setState(empty ? 'spent' : 'error');
        if (empty) onTokens(0);
      },
    );

    return () => {
      alive = false;
      handle?.cancel();
    };
    // `input` is rebuilt every render; the mount key is peekKey(input), so a
    // change of identity remounts rather than re-running this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  if (state === 'ready' && stills.length) {
    return <PreviewFilm stills={stills} secondsPerStill={perStill} />;
  }

  return (
    <div className="sample">
      {state === 'idle' && (
        <button className="ghost" onClick={() => setState('making')}>
          ▸ Sneak peek — free
        </button>
      )}
      {state === 'making' && (
        <div className="sub pulse">Sketching the scenes…</div>
      )}
      {state === 'spent' && (
        <div className="legend">
          You’ve used your free previews for now. They come back when you top up
          your credits.
        </div>
      )}
      {state === 'error' && (
        <div className="legend">
          We couldn’t sketch this one — you can still go ahead and make it.
        </div>
      )}
    </div>
  );
}
